import React from 'react'
import { TestIds } from '../../../../testIds'
import { FiltersCard } from '../../../../components/Filters/FilterCard'
import { ValueAndSetValue } from '../../../../components/Filters/types'
import { OrderFilters } from './types'
import Checkbox from '../../../../components/Checkbox'
import TextField from '../../../../components/TextField'
import MaterialSymbol from '../../../../components/MaterialSymbol'
import ChipMenu from '../../../../components/ChipMenu/ChipMenu'
import { FilterDropdown } from '../../../products/components/ProductFilter/ProductFilterDropdownLayout'
import { DesktopDateRangePicker } from '../../../products/components/ProductFilter/DesktopFilters'
import { formatAgreementDates } from '../../../sales-agreements/utils/formatAgreementDates'
import { useMediaQuery, Button, useTheme, Box, IconButton } from '@mui/material'
import { MobileFilterMenu } from '../../../products/components/ProductFilter/MobileFilters'
import { ComponentFilters } from '../../../products/components/ProductFilter'
import { colors } from '../../../../themes/colors'

type OrderFilterProps = ValueAndSetValue<OrderFilters>

const OrdersFilter: React.FC<OrderFilterProps> = ({
  dateRange,
  hideFarmlandsCardOrders,
  pickupOnly,
  seedPickup,
  searchText,
}) => {
  const [timeFilterOpen, setTimeFilterOpen] = React.useState(false)
  const theme = useTheme()
  const isSm = useMediaQuery(theme.breakpoints.down('sm'))
  const isLg = useMediaQuery(theme.breakpoints.up('lg'))

  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)
  const mobileFilters = React.useMemo(() => {
    return (
      <>
        <Button
          sx={{ width: '100%' }}
          startIcon={<MaterialSymbol>page_info</MaterialSymbol>}
          variant='outlined'
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
        >
          Filter orders
        </Button>
        <MobileFilterMenu
          components={[ComponentFilters.DATE]}
          title='Filter orders'
          backAction='Refine orders'
          selectedFilters={{
            dateRange: dateRange,
          }}
          filters={{
            categories: [],
          }}
          items={[]}
          open={mobileMenuOpen}
          handleClose={() => setMobileMenuOpen(false)}
        >
          <>
            <Checkbox
              label='Hide Farmlands Card orders'
              checked={hideFarmlandsCardOrders.value}
              onChange={(_, checked) => {
                seedPickup.setValue(false)
                pickupOnly.setValue(false)
                hideFarmlandsCardOrders.setValue(checked)
              }}
              data-testid={TestIds.feat.orders.filters.hideFarmlandsCardOrders}
            />
            <Checkbox
              label='Show pickup orders only'
              checked={pickupOnly.value}
              onChange={(_, checked) => {
                seedPickup.setValue(false)
                pickupOnly.setValue(checked)
                hideFarmlandsCardOrders.setValue(false)
              }}
              data-testid={TestIds.feat.orders.filters.pickUpOnlyCheckbox}
            />
            <Checkbox
              label='Show Seed pickup orders only'
              checked={seedPickup.value}
              onChange={(_, checked) => {
                seedPickup.setValue(checked)
                pickupOnly.setValue(false)
                hideFarmlandsCardOrders.setValue(false)
              }}
              data-testid={TestIds.feat.orders.filters.seedOnlyCheckbox}
            />
          </>
        </MobileFilterMenu>
      </>
    )
  }, [dateRange, isSm, mobileMenuOpen, pickupOnly])

  const desktopFilters = React.useMemo(() => {
    return (
      <Box display='flex' gap={3} alignItems={'center'} pb={0.5}>
        <ChipMenu
          label='Time period'
          open={timeFilterOpen}
          setOpen={setTimeFilterOpen}
          value={
            dateRange.value.from && dateRange.value.to
              ? formatAgreementDates(dateRange.value.from, dateRange.value.to)
              : 'Any'
          }
          testId={TestIds.components.filters.dateRangeChip}
          dropdown={
            <FilterDropdown
              title={'Time period'}
              onClear={() =>
                dateRange.setValue({
                  from: undefined,
                  to: undefined,
                })
              }
              onSubmit={() => {
                /* */
                setTimeFilterOpen(false)
              }}
            >
              <DesktopDateRangePicker
                dateRange={dateRange.value}
                handleDateRange={dateRange.setValue}
              />
            </FilterDropdown>
          }
        />
        <Checkbox
          label='Hide Farmlands Card orders'
          color={colors.primary700}
          sx={{ color: colors.primary700 }}
          checked={hideFarmlandsCardOrders.value}
          onChange={(_, checked) => {
            hideFarmlandsCardOrders.setValue(checked)
            pickupOnly.setValue(false)
            seedPickup.setValue(false)
          }}
          data-testid={TestIds.feat.orders.filters.hideFarmlandsCardOrders}
        />
        <Checkbox
          label='Show pickup orders only'
          color={colors.primary700}
          sx={{ color: colors.primary700 }}
          checked={pickupOnly.value}
          onChange={(_, checked) => {
            pickupOnly.setValue(checked)
            hideFarmlandsCardOrders.setValue(false)
            seedPickup.setValue(false)
          }}
          data-testid={TestIds.feat.orders.filters.pickUpOnlyCheckbox}
        />

        <Checkbox
          label='Show Seed pickup orders only'
          color={colors.primary700}
          sx={{ color: colors.primary700 }}
          checked={seedPickup.value}
          onChange={(_, checked) => {
            seedPickup.setValue(checked)
            pickupOnly.setValue(false)
            hideFarmlandsCardOrders.setValue(false)
          }}
          data-testid={TestIds.feat.orders.filters.seedOnlyCheckbox}
        />
      </Box>
    )
  }, [isSm, timeFilterOpen, searchText, dateRange, pickupOnly])

  return (
    <FiltersCard>
      <TextField
        sx={{ maxWidth: isLg ? '512px' : 'auto', height: '45px', pr: '3px' }}
        variant={'outlined'}
        placeholder='Search by Order#'
        value={searchText.value}
        testId={TestIds.feat.orders.filters.orderNumber}
        onChange={(event) => searchText.setValue(event.target.value)}
        inputProps={{
          maxLength: 100,
          type: 'number',
          onWheel: (e) => e.currentTarget.blur(),
        }}
        InputProps={{
          endAdornment: (
            <IconButton color='primary' title='Search'>
              <MaterialSymbol>search</MaterialSymbol>
            </IconButton>
          ),
        }}
      />
      {isSm ? mobileFilters : desktopFilters}
    </FiltersCard>
  )
}

export default OrdersFilter
